import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAuth } from '@/lib/auth'
import { useI18n } from '@/lib/i18n'
import { safeReturnTarget, signInUrlReturningTo } from '@/lib/return-target'

export function ForgetPasswordPage() {
  const [email, setEmail] = useState('')
  const { isLoading, forgetPassword } = useAuth()
  const { t } = useI18n()
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [searchParams] = useSearchParams()
  const signInUrl = signInUrlReturningTo(
    safeReturnTarget(searchParams.get('redirect'), '/dashboard'),
  )

  const handleSubmit = async () => {
    if (!email)
      return

    try {
      if (forgetPassword) {
        const success = await forgetPassword({ email })
        if (success) {
          setIsSubmitted(true)
        }
      }
      else {
        console.error('forgetPassword function is missing from useAuth')
      }
    }
    catch (e) {
      console.error(e)
    }
  }

  return (
    <div className="h-screen pt-16">
      <div className="mx-auto w-full max-w-sm pt-24 md:max-w-md">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg md:text-xl">
              {t('auth.forgotPassword.title')}
            </CardTitle>
            <CardDescription className="text-xs md:text-sm">
              {t('auth.forgotPassword.description')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isSubmitted ? (
              <div className="text-center text-sm">
                <p className="mb-4 text-muted-foreground">
                  If an account exists for {email}, you will receive a password reset link shortly.
                </p>
                <Button variant="outline" asChild className="w-full">
                  <Link to={signInUrl}>{t('auth.forgotPassword.signIn')}</Link>
                </Button>
              </div>
            ) : (
              <div className="grid gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="email">{t('auth.forgotPassword.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.forgotPassword.emailPlaceholder')}
                    required
                    onChange={(e) => {
                      setEmail(e.target.value)
                    }}
                    value={email}
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isLoading}
                  onClick={handleSubmit}
                >
                  {isLoading ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    t('auth.forgotPassword.button')
                  )}
                </Button>
              </div>
            )}
          </CardContent>
          {!isSubmitted && (
            <CardFooter className="flex justify-center">
              <div className="text-sm text-muted-foreground">
                <Link to={signInUrl} className="hover:underline">
                  {t('auth.forgotPassword.signIn')}
                </Link>
              </div>
            </CardFooter>
          )}
        </Card>
      </div>
    </div>
  )
}
